import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../../iba-components/Button'
import { Checkbox } from '../../../../iba-components/Checkbox'
import { Input } from '../../../../iba-components/Input'
import { Select } from '../../../../iba-components/Select'
import { INIT_SIGN_UP_INFO, MAX_LENGTH, getPlanSelectList } from '../constant'
import PropTypes from 'prop-types'
import { phoneNumberReg, eMailReg } from '../../../api/common'
import { fetchClientThemes } from '../../../api/theme'
import { Loading } from '../../Loading'

import './signUpClientView.css'
import styles from './signUpClientView.module.css'

const getValueOfSpecifiedKey = (dataList, findKey, findValue, specifiedKey) => {
    const findObject = dataList.find((data) => {
        return data[findKey] === findValue
    })

    return findObject ? findObject[specifiedKey] : ''
}

const SignUpClientView = ({ onSubmit, clientInfo, userInfo }) => {
    const { t } = useTranslation()

    const [signUpInfo, setSignUpInfo] = useState(
        clientInfo && Object.keys(clientInfo).length ? clientInfo : INIT_SIGN_UP_INFO
    )

    const [loading, setLoading] = useState(false)
    const [clientThemList, setClientThemList] = useState([])

    const basicInfo = [
        {
            label: t(`signUpClient.companyName`), // required, <=60
            name: 'companyname',
            value: signUpInfo.companyname,
            required: true,
            hasError: true,
            maxLength: MAX_LENGTH.mid
        },
        {
            label: t(`signUpClient.contractName1`), // required, <=60
            name: 'contractrepresentativename1',
            value: signUpInfo.contractrepresentativename1,
            maxLength: MAX_LENGTH.mid
        },
        {
            label: t(`signUpClient.industries`), // required
            name: 'industries',
            value: signUpInfo.industries,
            required: true,
            hasError: true,
            maxLength: MAX_LENGTH.mid
        },
        {
            label: t(`signUpClient.postalCode`), // only code eng num, <=50
            name: 'postalcode',
            value: signUpInfo.postalcode,
            maxLength: MAX_LENGTH.min,
            inputLimited: true
        },
        {
            label: t(`signUpClient.companyAddress`), // <=100
            name: 'companyaddress',
            value: signUpInfo.companyaddress,
            maxLength: MAX_LENGTH.max
        },
        {
            label: `${t(`signUpClient.phoneNumber`)} ${t(`signUpClient.phoneNumberTip`)}`, // only code eng num, <=50
            name: 'phonenumber',
            value: signUpInfo.phonenumber,
            maxLength: MAX_LENGTH.min,
            inputLimited: true,
            phoneNumberCheck: true
        },
        {
            label: t(`signUpClient.contractEmail1`), // required, only code eng num, <=100, email
            name: 'contractrepresentativeemail1',
            value: signUpInfo.contractrepresentativeemail1,
            required: true,
            hasError: true,
            maxLength: MAX_LENGTH.max,
            inputLimited: true,
            mailChecked: true
        },
        {
            label: t(`signUpClient.contractPlan`), // required
            name: 'contractplan',
            value: getValueOfSpecifiedKey(getPlanSelectList(t), 'code', signUpInfo.contractplan, 'content'),
            isSelect: true,
            selectList: getPlanSelectList(t),
            maxLength: MAX_LENGTH.max,
            isRequired: true
        },
        {
            label: t(`signUpClient.clientTheme`),
            name: 'clientthememapid',
            value: getValueOfSpecifiedKey(clientThemList, 'code', signUpInfo.clientthememapid, 'content'),
            isSelect: true,
            selectList: clientThemList,
            isRequired: false
        },
        {
            label: t(`signUpClient.trialLabel`),
            name: 'is_trial',
            value: signUpInfo.is_trial,
            isCheckBox: true
        }
    ]

    const handleChangeCheckBox = (checked, name) => {
        setSignUpInfo((prevSignUpInfo) => {
            const updater = { ...prevSignUpInfo }

            return {
                ...updater,
                [name]: checked
            }
        })
    }

    const [touchedList, setTouchedList] = useState({})

    const [mailError, setMailError] = useState({
        contractrepresentativeemail1_mail_error: false
    })

    const [phoneNumError, setPhoneNumError] = useState(false)

    const [submitted, setSubmitted] = useState(false)

    const onSubmitClient = () => {
        setSubmitted(true)

        const hasEmailError = Object.values(mailError).filter((value) => value === true)

        if (
            signUpInfo.companyname &&
            signUpInfo.industries &&
            signUpInfo.contractrepresentativeemail1 &&
            signUpInfo.contractplan &&
            !hasEmailError.length &&
            !phoneNumError
        ) {
            const postData = {}

            Object.keys(signUpInfo).map((ele) => {
                if (signUpInfo[ele] !== '') {
                    postData[ele] = signUpInfo[ele]
                }
                return postData
            })

            postData.createdby = userInfo.username

            if (clientInfo) {
                postData.clientid = postData.id
                delete postData.id
                onSubmit('update', postData)
            } else {
                onSubmit('insert', postData)
            }
        }
    }

    const onChangeInput = (val, ele) => {
        if (ele.inputLimited) {
            setSignUpInfo({
                ...signUpInfo,
                [ele.name]: val.replace(
                    /[^\a-\z\A-\Z0-9\!\@\#\$\%\^\&\*\(\)\-\_\=\+\[\]\{\}\\\|\;\:\'\"\,\<\.\>\/\?\`\~\ ]/g, // eslint-disable-line
                    ''
                )
            })
        } else {
            setSignUpInfo({
                ...signUpInfo,
                [ele.name]: val
            })
        }

        // check email
        if (val.trim() && ele.mailChecked) checkEmailValidate(ele.name, val.trim())

        // check phone number
        if (val.trim() && ele.phoneNumberCheck) checkphoneNumberValidate(ele.name, val.trim())
    }

    // check email
    const checkEmailValidate = (name, val) => {
        const pattern = eMailReg
        !pattern.test(val)
            ? setMailError({ ...mailError, [`${name}_mail_error`]: true })
            : setMailError({ ...mailError, [`${name}_mail_error`]: false })
    }

    // check phone number
    const checkphoneNumberValidate = (name, val) => {
        const pattern = phoneNumberReg
        setPhoneNumError(!pattern.test(val))
    }

    // input funcs
    const onBlurInput = (e) => {
        setTouchedList({
            ...touchedList,
            [`${e.target.name}_touched`]: true
        })
    }

    // check select/calender empty
    const validateIsEmpty = (val, touched, isRequired) => isRequired && !val && (touched || submitted)

    // FIXME: 条件を綺麗に整理したい
    const checkErrorInput = (ele) => {
        return (
            (ele.hasError &&
                (!ele.value?.trim() || mailError[`${ele.name}_mail_error`]) &&
                (touchedList[`${ele.name}_touched`] || submitted)) ||
            (ele.mailChecked &&
                !!ele.value &&
                mailError[`${ele.name}_mail_error`] &&
                touchedList[`${ele.name}_touched`]) ||
            (ele.phoneNumberCheck && !!ele.value && phoneNumError && touchedList[`${ele.name}_touched`])
        )
    }
    const getErrorMsg = (ele) => {
        const isEmpty = !ele.value?.trim()
        const isPhone = ele.name === 'phonenumber'
        const label = isPhone ? t(`signUpClient.contractNumber1`) : ele.label

        if (isEmpty) return `${label}${t(`signUpClient.warningMsg.isRequired`)}`

        return ele.name === 'contractrepresentativeemail1'
            ? t(`signUpClient.warningMsg.incorrectEmailFormat`)
            : isPhone
            ? t(`signUpClient.warningMsg.incorrectPhoneNumberFormat`)
            : ''
    }

    const getSignUpClientInput = (ele) => (
        <div className="col-12 col-sm-6 col-md-4" key={ele.name}>
            <div className="sign-up-input-container">
                <Input
                    label={ele.label}
                    name={ele.name}
                    required={ele.required}
                    value={ele.value}
                    width={240}
                    maxLength={ele.maxLength}
                    onChange={(value) => onChangeInput(value, ele)}
                    onBlur={onBlurInput}
                    errorMsg={checkErrorInput(ele) ? getErrorMsg(ele) : ''}
                />
            </div>
        </div>
    )

    const getSignUpClientSelect = (ele) => {
        return (
            <div className="col-12 col-sm-6 col-md-4" key={ele.name}>
                <div className="sign-up-input-container">
                    <Select
                        itemKey="code"
                        dataList={ele.selectList}
                        handleChangeSelect={(value) => {
                            setSignUpInfo({
                                ...signUpInfo,
                                [ele.name]: value
                            })
                        }}
                        isClear={!ele.isRequired}
                        inputProps={{
                            label: ele.label,
                            required: ele.isRequired,
                            placeholder: `Please select`,
                            value: ele.value,
                            onBlur: () => {
                                if (!loading) {
                                    setTouchedList({
                                        ...touchedList,
                                        [`${ele.name}_touched`]: true
                                    })
                                }
                            },
                            hasError: validateIsEmpty(ele.value, touchedList[`${ele.name}_touched`], ele.isRequired)
                        }}
                        selectedValue={signUpInfo[ele.name]}
                    />
                    {validateIsEmpty(ele.value, touchedList[`${ele.name}_touched`], ele.isRequired) ? (
                        <span className={styles.form_error}>{`${ele.label}${t(
                            `signUpClient.warningMsg.selectIsRequired`
                        )}`}</span>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        )
    }

    const getSignUpClientCheckBox = (element) => (
        <div className="col-12 col-sm-6 col-md-4" key={element.name}>
            <div className="sign-up-input-container">
                <Checkbox
                    label={element.label}
                    checked={element.value}
                    onChange={(checked) => handleChangeCheckBox(checked, element.name)}
                />
            </div>
        </div>
    )

    useEffect(() => {
        async function fetchClientThemeList() {
            setLoading(true)
            const res = await fetchClientThemes()
            setTimeout(() => {
                // add delay to avoid the mandatory check on client theme select during loading when sign up new client
                setLoading(false)
            }, 100)
            if (res) {
                const clientThemeList = res.data?.map((item) => {
                    return {
                        content: item.name,
                        code: `${item.id}`
                    }
                })
                setClientThemList(clientThemeList)
            }
        }
        fetchClientThemeList()
    }, [])

    return (
        <>
            <Loading visible={loading}>
                <div className="sign-up-view">
                    <div className="row">
                        {basicInfo.map((element) =>
                            element.isSelect
                                ? getSignUpClientSelect(element)
                                : element.isCheckBox
                                ? getSignUpClientCheckBox(element)
                                : getSignUpClientInput(element)
                        )}
                    </div>

                    <div className="section">
                        <div className="d-flex sign-up-btn">
                            <Button
                                width="96px"
                                label={clientInfo ? t(`clientManagement.update`) : t(`signUpClient.signUp`)}
                                onClick={onSubmitClient}
                            />
                        </div>
                    </div>
                </div>
            </Loading>
        </>
    )
}

SignUpClientView.propTypes = {
    signUpClientData: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    clientInfo: PropTypes.object,
    id: PropTypes.number
}

export default SignUpClientView
